/**
 * CommandPalette — search-UX §4.1.
 *
 * Universal ⌘K / Ctrl+K command palette.  Web-desktop / power-user
 * accelerator that cuts multi-tap paths down to two keystrokes.
 * Linear + Notion are the canonical pattern.
 *
 * Sources of "hits":
 *   - Actions (hardcoded): Recall an item, Go to Scan history, …
 *   - Screens (navigation targets): Browse, Crews, Notifications, …
 *   - Items: fuzzy match over `inventoryItemsEnhanced` (already in
 *     the store from BrowseScreen load).
 *   - Tags: fuzzy match over `selectAllTags` corpus.
 *
 * Mobile note: per `search-ux.md §4.1`, deferred for v1 — we render
 * the modal even on mobile, but the keyboard shortcut is web-only
 * and there's no visible affordance to open it from mobile.  A
 * separate / mobile entry point can come later.
 *
 * Keyboard wiring: a document-level `keydown` listener on web only
 * (`Platform.OS === 'web'`).  We register it once at component
 * mount; cleanup on unmount.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useAppSelector } from '../store/store';
import {
  selectAllTags,
  selectInventoryItemsEnhanced,
} from '../features/browse/browseSlice';
import { fpColors } from '../styles/theme';

interface Hit {
  /** Stable key for FlatList. */
  key: string;
  /** Section header (Actions / Items / Tags / Screens). */
  section: 'Actions' | 'Screens' | 'Items' | 'Tags';
  /** Display label. */
  label: string;
  /** Optional sub-label (e.g. custodian for an item). */
  subLabel?: string;
  /** Optional MaterialIcons name for the row icon. */
  icon?: string;
  /** Callback invoked when the user picks this hit. */
  onPick: () => void;
}

const MAX_PER_SECTION = 5;

export const CommandPalette: React.FC = () => {
  const navigation = useNavigation<any>();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const allItems = useAppSelector(selectInventoryItemsEnhanced);
  const allTags = useAppSelector(selectAllTags);

  // ── Keyboard wiring (web only) ──────────────────────────────────
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (typeof document === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const hits = useMemo<Hit[]>(() => {
    const q = query.trim().toLowerCase();
    const matches = (s: string): boolean =>
      !q || s.toLowerCase().includes(q);

    // ── Actions (hardcoded — small, curated set) ──
    const actions: Hit[] = ([
      {
        key: 'act-recall',
        section: 'Actions',
        label: 'Recall an item from a borrower',
        icon: 'undo',
        onPick: () => {
          navigation.navigate('MainTabs', { screen: 'Share' });
          setOpen(false);
        },
      },
      {
        key: 'act-scan-history',
        section: 'Actions',
        label: 'Open Scan history',
        icon: 'history',
        onPick: () => {
          navigation.navigate('ScanHistory');
          setOpen(false);
        },
      },
      {
        key: 'act-cross-group-search',
        section: 'Actions',
        label: 'Search across circles',
        icon: 'search',
        onPick: () => {
          navigation.navigate('CrossGroupSearch');
          setOpen(false);
        },
      },
      {
        key: 'act-add-item',
        section: 'Actions',
        label: 'Add a new item',
        icon: 'add-circle',
        onPick: () => {
          navigation.navigate('Upload');
          setOpen(false);
        },
      },
    ] as Hit[]).filter((a) => matches(a.label));

    // ── Screens ──
    const screens: Hit[] = ([
      { key: 'scr-home', section: 'Screens', label: 'Go to Dashboard', icon: 'home', onPick: () => navigation.navigate('MainTabs', { screen: 'Home' }) },
      { key: 'scr-browse', section: 'Screens', label: 'Go to Items (Browse)', icon: 'grid-view', onPick: () => navigation.navigate('MainTabs', { screen: 'Browse' }) },
      { key: 'scr-share', section: 'Screens', label: 'Go to Share', icon: 'swap-horiz', onPick: () => navigation.navigate('MainTabs', { screen: 'Share' }) },
      { key: 'scr-circles', section: 'Screens', label: 'Go to Circles', icon: 'people', onPick: () => navigation.navigate('MainTabs', { screen: 'Community' }) },
      { key: 'scr-notif', section: 'Screens', label: 'Go to Notifications', icon: 'notifications', onPick: () => navigation.navigate('Notifications') },
      { key: 'scr-profile', section: 'Screens', label: 'Go to Profile', icon: 'person', onPick: () => navigation.navigate('Profile') },
    ] as Hit[]).filter((s) => matches(s.label));
    screens.forEach((s) => {
      const orig = s.onPick;
      s.onPick = () => {
        orig();
        setOpen(false);
      };
    });

    // ── Items (filtered + capped) ──
    const items: Hit[] = (allItems as any[])
      .filter((it) => matches(String(it.item_name ?? '')))
      .slice(0, MAX_PER_SECTION)
      .map((it) => ({
        key: `item-${it.id}`,
        section: 'Items',
        label: String(it.item_name ?? 'Untitled item'),
        subLabel: it.item_category
          ? String(it.item_category)
          : it.location
            ? String(it.location)
            : undefined,
        icon: 'inventory',
        onPick: () => {
          navigation.navigate('ItemDetail', { itemId: it.id });
          setOpen(false);
        },
      }));

    // ── Tags (filtered + capped) ──
    const tags: Hit[] = allTags
      .filter((t) => matches(t.tag))
      .slice(0, MAX_PER_SECTION)
      .map((t) => ({
        key: `tag-${t.tag}`,
        section: 'Tags',
        label: t.tag,
        subLabel: t.count === 1 ? '1 item' : `${t.count} items`,
        icon: 'sell',
        onPick: () => {
          navigation.navigate('MainTabs', {
            screen: 'Browse',
            params: { layout: 'tag', tag: t.tag },
          });
          setOpen(false);
        },
      }));

    return [
      ...actions.slice(0, MAX_PER_SECTION),
      ...screens.slice(0, MAX_PER_SECTION),
      ...items,
      ...tags,
    ];
  }, [query, navigation, allItems, allTags]);

  // Insert section dividers for the FlatList renderer.
  const sectioned = useMemo<Array<Hit | { divider: string }>>(() => {
    const out: Array<Hit | { divider: string }> = [];
    let lastSection: string | null = null;
    for (const h of hits) {
      if (h.section !== lastSection) {
        out.push({ divider: h.section });
        lastSection = h.section;
      }
      out.push(h);
    }
    return out;
  }, [hits]);

  if (!open) return null;

  return (
    <Modal
      visible={open}
      transparent
      animationType="fade"
      onRequestClose={() => setOpen(false)}
    >
      <Pressable
        style={styles.scrim}
        onPress={() => setOpen(false)}
        accessibilityRole="button"
        accessibilityLabel="Close command palette"
      >
        <Pressable
          style={styles.sheet}
          onPress={(e) => e.stopPropagation()}
          testID="command-palette"
        >
          {/* Header — search input + close affordance */}
          <View style={styles.header}>
            <MaterialIcons name="search" size={18} color={fpColors.outline} />
            <TextInput
              autoFocus
              value={query}
              onChangeText={setQuery}
              placeholder="Type to search items, tags, screens…"
              placeholderTextColor={fpColors.outline}
              style={styles.input}
              autoCorrect={false}
              autoCapitalize="none"
              testID="command-palette-input"
            />
            <View style={styles.kbdHint}>
              <Text style={styles.kbdHintText}>ESC</Text>
            </View>
          </View>

          {/* Results */}
          {sectioned.length === 0 ? (
            <View style={styles.empty}>
              <Text style={styles.emptyText}>
                {query
                  ? `No matches for "${query}"`
                  : 'Type to search'}
              </Text>
            </View>
          ) : (
            <FlatList
              data={sectioned}
              keyExtractor={(row) =>
                'divider' in row ? `div-${row.divider}` : row.key
              }
              keyboardShouldPersistTaps="handled"
              renderItem={({ item }) => {
                if ('divider' in item) {
                  return (
                    <Text style={styles.divider}>{item.divider.toUpperCase()}</Text>
                  );
                }
                return (
                  <Pressable
                    onPress={item.onPick}
                    style={({ pressed }) => [
                      styles.row,
                      pressed && styles.rowPressed,
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={item.label}
                    testID={`command-palette-row-${item.key}`}
                  >
                    {item.icon ? (
                      <MaterialIcons
                        name={item.icon as any}
                        size={16}
                        color={fpColors.onSurfaceVariant}
                        style={{ marginRight: 10 }}
                      />
                    ) : null}
                    <Text style={styles.rowLabel} numberOfLines={1}>
                      {item.label}
                    </Text>
                    {item.subLabel ? (
                      <Text style={styles.rowSubLabel} numberOfLines={1}>
                        {item.subLabel}
                      </Text>
                    ) : null}
                  </Pressable>
                );
              }}
            />
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    backgroundColor: 'rgba(31, 26, 34, 0.55)',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: 80,
  },
  sheet: {
    width: '92%',
    maxWidth: 640,
    maxHeight: 520,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowOffset: { width: 0, height: 12 },
    shadowRadius: 24,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: fpColors.outlineVariant,
    gap: 8,
  },
  input: {
    flex: 1,
    fontSize: 15,
    color: fpColors.onSurface,
    padding: 0,
    margin: 0,
  },
  kbdHint: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  kbdHintText: {
    fontSize: 10,
    fontWeight: '700',
    color: fpColors.onSurfaceVariant,
  },
  empty: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  divider: {
    fontSize: 10,
    fontWeight: '700',
    color: fpColors.onSurfaceVariant,
    letterSpacing: 0.6,
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  rowPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  rowLabel: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
  },
  rowSubLabel: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
});

export default CommandPalette;
